import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
    useEffect,
} from "react";
import { IClientError } from "@finbackoffice/websocket-client";
import { IWalletResponse } from "@finbackoffice/clientbff-client";
import { Currency, FiatCurrencies, isCryptoCurrency, WalletType } from "@finbackoffice/enums";
import {
    useRuntimeConfig,
    BrowserStorageContext,
    AuthContext,
    BrowserStorageIdsEnum,
    useWebsocketUpdates,
} from "@finbackoffice/site-core";
import { getCurrencyTempId } from "../utils/helpers";
import { UserAccountContextV2 } from "./user-account-v2.provider";
import { ConfigContextV2 } from "./config-v2.provider";

const convertToLocalStorageItem = (wallet: Partial<IWalletResponse>) => {
    return { type: wallet.type, currency: wallet.currency };
};

export interface IWalletsState {
    data: IWalletResponse[];
    loading: boolean;
}

export interface IWalletsContext {
    currentWallet: Partial<IWalletResponse> | null;
    currentCurrency: Currency | undefined;
    currentBalance: string;
    currentWalletId: number | undefined;
    isCurrentWalletIdValid: boolean;
    userWallets: IWalletsState;
    userCurrency: Currency | "";
    currencyOfExchange: Currency | "";
    siteSupportedWallets: (
        | IWalletResponse
        | Pick<IWalletResponse, "balance" | "currency" | "type">
    )[];
    currenciesEnabledServices: Partial<Record<Currency, { withdraw: boolean; deposit: boolean }>>;
    siteSupportedWalletCurrencies: Currency[];
    setCurrentWallet: (wallet: Partial<IWalletResponse> | null) => void;
    setSelectedWallet: (payload: Partial<IWalletResponse>) => void;
}
export const WalletsContext = createContext<IWalletsContext>(null as any);

export const WalletsProvider: FC<PropsWithChildren> = ({ children }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { sitePaymentsCurrencyConfig, siteConfigReady } = useContext(ConfigContextV2);
    const { set, get } = useContext(BrowserStorageContext);
    const { userToken, isUserLoggedIn } = useContext(AuthContext);
    const { userCurrency, userData, userDataLoading, updateUser, loadProfile } =
        useContext(UserAccountContextV2);

    const [siteSupportedWallets, setSiteSupportedWallets] = useState<
        (IWalletResponse | Pick<IWalletResponse, "balance" | "currency" | "type">)[]
    >([]);
    const [currentWallet, setCurrentWallet] = useState<Partial<IWalletResponse> | null>(null);

    const userWallets = useMemo(
        () => ({
            data: userData?.wallets || [],
            loading: userDataLoading,
        }),
        [userData?.wallets, userDataLoading],
    );

    const walletConfigs = useMemo(() => COMMON_SITE_CONFIGS.wallet, [COMMON_SITE_CONFIGS]);
    const fiatEnabled = useMemo(() => COMMON_SITE_CONFIGS.wallet.fiat, [COMMON_SITE_CONFIGS]);

    const currencyOfExchange = useMemo(
        () => (fiatEnabled ? userCurrency : COMMON_SITE_CONFIGS.wallet.fxCurrency),
        [fiatEnabled, userCurrency, COMMON_SITE_CONFIGS.wallet.fxCurrency],
    );

    useEffect(() => {
        if (!userData?.wallets || userData?.wallets.length === 0) {
            setCurrentWallet(null);
        }
    }, [userData?.wallets]);

    const setSelectedWallet = useCallback(
        (payload: Partial<IWalletResponse>) => {
            let newCurrentWallet = payload;
            if (userToken && !payload.id) {
                const userWallet = userData?.wallets.find(
                    (wallet) =>
                        wallet.type === payload?.type && wallet.currency === payload.currency,
                );
                if (userWallet) {
                    newCurrentWallet = userWallet;
                }
            }

            setCurrentWallet(newCurrentWallet);

            set(BrowserStorageIdsEnum.WALLET, JSON.stringify(convertToLocalStorageItem(payload)));
        },
        [userToken, set, userData?.wallets],
    );

    const updateBalance = useCallback(
        (payload: IWalletResponse) => {
            if (!userData?.wallets) return;

            const walletIndex = userData.wallets.findIndex(
                (w: IWalletResponse) => w.id === payload.id,
            );

            if (walletIndex === -1) {
                // Wallet not found in list - this could be a new wallet from first deposit
                // Reload the profile to get the updated wallets list
                void loadProfile();
                // Update currentWallet if it matches by currency
                setCurrentWallet((prev) => (prev?.currency === payload.currency ? payload : prev));
                return;
            }

            // Only update if balance actually changed
            if (userData.wallets[walletIndex].balance === payload.balance) {
                return;
            }

            // Immutable update
            const updatedWallets = [...userData.wallets];
            updatedWallets[walletIndex] = payload;

            // Update the source through UserAccount's updateUser
            updateUser({ wallets: updatedWallets });

            // Update currentWallet if it matches by ID
            setCurrentWallet((prev) => (prev?.id === payload.id ? payload : prev));
        },
        [userData?.wallets, loadProfile, updateUser],
    );

    // Socket subscription for wallet updates
    const handleWalletSocketUpdate = useCallback(
        (data: IWalletResponse, error: IClientError) => {
            if (error) {
                console.error("Wallet socket error:", error);
                return;
            }
            updateBalance(data);
        },
        [updateBalance],
    );

    const socketParams = useMemo(() => ({ token: userToken || "" }), [userToken]);

    const walletSocket = useWebsocketUpdates(
        "wallet",
        handleWalletSocketUpdate,
        { subscribeByDefault: false },
        socketParams,
    );

    useEffect(() => {
        if (userToken) {
            walletSocket.subscribe();
        } else {
            walletSocket.unsubscribe();
        }
    }, [userToken, walletSocket]);

    const siteSupportedWalletCurrencies = useMemo(() => {
        return siteSupportedWallets
            .filter((wallet) => wallet.type === WalletType.Balance && wallet.currency)
            .map((wallet) => wallet.currency as Currency);
    }, [siteSupportedWallets]);

    const currenciesEnabledServices = useMemo(() => {
        return Object.entries(sitePaymentsCurrencyConfig || {}).reduce(
            (acc, [currency, config]) => {
                return {
                    ...acc,
                    [currency]: {
                        withdraw: config.withdrawal.enabled,
                        deposit: config.deposit.enabled,
                    },
                };
            },
            {},
        );
    }, [sitePaymentsCurrencyConfig]);

    const isCurrencySupported = useCallback(
        (val: Currency) =>
            siteSupportedWallets.find((wallet) => wallet.currency === val) !== undefined,
        [siteSupportedWallets],
    );

    const filterWalletsByConfig = useCallback(
        (wallets: (IWalletResponse | Pick<IWalletResponse, "balance" | "currency" | "type">)[]) => {
            let filtered = [...wallets];
            // Filter out crypto if not enabled
            if (!walletConfigs._crypto.enable) {
                filtered = filtered.filter((wallet) => {
                    return wallet.currency && !isCryptoCurrency(wallet.currency);
                });
            }

            // Filter out fiat if not enabled
            if (!walletConfigs.fiat) {
                filtered = filtered.filter((wallet) => {
                    return wallet.currency && !FiatCurrencies.includes(wallet.currency);
                });
            }

            // Filter fiat currencies based on fiatActiveOnly when user is logged in
            if (walletConfigs.fiat && isUserLoggedIn && walletConfigs.fiatActiveOnly) {
                filtered = [
                    ...filtered.filter((wallet) => {
                        return wallet.currency && !FiatCurrencies.includes(wallet.currency);
                    }),
                    ...userWallets.data.filter((wallet) => {
                        return wallet.currency && FiatCurrencies.includes(wallet.currency);
                    }),
                ];
            }

            // Filter out zero balance fiat wallets when user currency is crypto
            if (userCurrency && isCryptoCurrency(userCurrency)) {
                filtered = filtered.filter((wallet) => {
                    return (
                        wallet.currency &&
                        !(
                            FiatCurrencies.includes(wallet.currency) &&
                            parseFloat(wallet.balance) === 0
                        )
                    );
                });
            }

            return filtered;
        },
        [
            isUserLoggedIn,
            userCurrency,
            userWallets.data,
            walletConfigs._crypto.enable,
            walletConfigs.fiat,
            walletConfigs.fiatActiveOnly,
        ],
    );

    const initSupportedWallets = useCallback(() => {
        if (!sitePaymentsCurrencyConfig) return;

        let wallets: (IWalletResponse | Pick<IWalletResponse, "balance" | "currency" | "type">)[] =
            [];

        // Build wallets list from currency config
        (Object.keys(sitePaymentsCurrencyConfig) as Currency[]).forEach((currency) => {
            const currencyConfig = sitePaymentsCurrencyConfig[currency];
            const balanceWallet = userWallets.data.find(
                (item) => item.type === WalletType.Balance && item.currency === currency,
            );
            const bonusWallet = userWallets.data.find(
                (item) => item.type === WalletType.Bonus && item.currency === currency,
            );

            const balance = parseFloat(balanceWallet?.balance || "0");
            const bonusBalance = parseFloat(bonusWallet?.balance || "0");

            // Add balance wallet if has balance OR currency is enabled
            if (balance > 0 || currencyConfig?.enabled) {
                const wallet = balanceWallet ?? {
                    currency: currency as Currency,
                    balance: "0",
                    type: WalletType.Balance,
                    id: getCurrencyTempId(currency),
                    version: -1,
                };
                wallets.push(wallet);
            }

            // Add bonus wallet if has balance
            if (bonusWallet && bonusBalance > 0) {
                wallets.push(bonusWallet);
            }
        });

        // Apply config filters
        wallets = filterWalletsByConfig(wallets);

        // Sort by order
        const sorted = wallets.sort((a, b) => {
            if (a.currency && b.currency) {
                return (
                    (sitePaymentsCurrencyConfig?.[a.currency]?.order || 0) -
                    (sitePaymentsCurrencyConfig?.[b.currency]?.order || 0)
                );
            }
            return -1;
        });

        setSiteSupportedWallets(sorted);
    }, [sitePaymentsCurrencyConfig, userWallets.data, filterWalletsByConfig]);

    const initSelectedWallet = useCallback(() => {
        let localStorageWallet: Partial<IWalletResponse> | null = get(BrowserStorageIdsEnum.WALLET);
        const isStorageWalletBonus = localStorageWallet?.type === WalletType.Bonus;
        const isStorageWalletSupported =
            localStorageWallet?.currency && isCurrencySupported(localStorageWallet.currency);
        const isUserCurrencyCrypto = userCurrency && isCryptoCurrency(userCurrency);

        if ((!isUserLoggedIn && isStorageWalletBonus) || !isStorageWalletSupported) {
            localStorageWallet = null;
        }

        const defaultCryptoWallet = siteSupportedWallets.find(
            (wallet) => wallet.currency === COMMON_SITE_CONFIGS.wallet._crypto.default,
        );

        const getNewWalletValue = () => {
            if (localStorageWallet?.type) {
                return localStorageWallet;
            } else if (
                defaultCryptoWallet &&
                (!fiatEnabled || (fiatEnabled && isUserCurrencyCrypto))
            ) {
                return defaultCryptoWallet;
            } else {
                return siteSupportedWallets[0];
            }
        };

        const newSelectedWallet = getNewWalletValue();

        setSelectedWallet(newSelectedWallet);
    }, [
        COMMON_SITE_CONFIGS.wallet._crypto.default,
        fiatEnabled,
        get,
        isCurrencySupported,
        setSelectedWallet,
        siteSupportedWallets,
        userCurrency,
        isUserLoggedIn,
    ]);

    useEffect(() => {
        if (siteConfigReady) {
            initSupportedWallets();
        }
    }, [siteConfigReady, initSupportedWallets]);

    useEffect(() => {
        if (siteSupportedWallets.length) {
            if (!currentWallet?.currency) {
                initSelectedWallet();
            } else {
                const wallet = siteSupportedWallets.find(
                    (item) =>
                        item.type === currentWallet?.type &&
                        item.currency === currentWallet?.currency,
                );
                if (wallet) {
                    setSelectedWallet(wallet);
                } else {
                    initSelectedWallet();
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteSupportedWallets]);

    const value = useMemo(
        () => ({
            currentWallet,
            currentCurrency: currentWallet?.currency,
            currentBalance: currentWallet?.balance || "0",
            isCurrentWalletIdValid: !!(currentWallet?.id && currentWallet?.id > 0),
            userWallets,
            userCurrency,
            currencyOfExchange,
            currentWalletId: currentWallet?.id,
            siteSupportedWallets,
            siteSupportedWalletCurrencies,
            currenciesEnabledServices,
            setCurrentWallet,
            setSelectedWallet,
        }),
        [
            currentWallet,
            userWallets,
            userCurrency,
            currencyOfExchange,
            siteSupportedWallets,
            siteSupportedWalletCurrencies,
            currenciesEnabledServices,
            setSelectedWallet,
        ],
    );
    return <WalletsContext.Provider value={value}>{children}</WalletsContext.Provider>;
};
